import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ClipboardCheck, Copy, Download, FileCheck2, FileSpreadsheet, KeyRound, ShieldCheck, UserCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import ModuleAccessDenied from '../../components/ui/ModuleAccessDenied';
import ComplianceReportView from '../../components/compliance/ComplianceReportView';
import ChecklistView from '../../components/compliance/ChecklistView';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import complianceService from '../../services/compliance';
import orchardService from '../../services/orchard';
import { errorMessage } from '../../utils/apiResponse';
import { dateTimeLocalToISO, formatDateTimeInTz, formatLocalDateInTz, getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import type {
  AuditEngagement, AutoChecks, Checklist, ComplianceAccess, CompliancePack, ComplianceReport, EngagementStatus, SectionKey,
} from '../../types/compliance';

/**
 * /compliance — what an auditor asks for, out of records the org already keeps.
 *
 *   Season pack      preview the report, then freeze it as a pack (PDF + CSV)
 *   Auditor access   read-only access for one named auditor, for a window
 *   Self-assessment  a standard's checklist, answered against the records
 *
 * Gated on the seats that manage the records (spray admin, timesheet HR,
 * farm_jobs admin/supervisor); each section appears only with its own seat.
 */

type TabKey = 'pack' | 'auditors' | 'checklist';

const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const STATUS_STYLE: Record<EngagementStatus, string> = {
  active: 'bg-green-100 text-green-800',
  scheduled: 'bg-blue-100 text-blue-800',
  expired: 'bg-neutral-100 text-neutral-600',
  revoked: 'bg-red-100 text-red-800',
};

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-sm font-medium text-neutral-700">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-neutral-500">{hint}</span>}
    </label>
  );
}

const inputClass = 'w-full min-w-0 rounded-lg border border-neutral-300 px-3 py-2 text-sm';

interface Scope {
  start: string;
  end: string;
  sections: SectionKey[];
  farmIds: number[];
  pseudonymise: boolean;
}

/** Period, farms, sections and the pseudonymise switch — shared by packs and auditor access. */
function ScopeFields({ access, farms, scope, onChange, pseudonymiseHint }: {
  access: ComplianceAccess; farms: { id: number; name: string }[]; scope: Scope; onChange: (s: Scope) => void; pseudonymiseHint: string;
}) {
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter(x => x !== v) : [...list, v]);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Records from"><input type="date" className={inputClass} value={scope.start} max={scope.end} onChange={e => onChange({ ...scope, start: e.target.value })} /></Field>
        <Field label="To"><input type="date" className={inputClass} value={scope.end} min={scope.start} onChange={e => onChange({ ...scope, end: e.target.value })} /></Field>
      </div>
      <fieldset>
        <legend className="mb-1 text-sm font-medium text-neutral-700">Sections</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {access.sections.map(s => (
            <label key={s.key} className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={scope.sections.includes(s.key)} onChange={() => onChange({ ...scope, sections: toggle(scope.sections, s.key) })} />
              {s.label}
            </label>
          ))}
        </div>
        {access.sections.length < access.all_sections.length && (
          <p className="mt-1 text-xs text-neutral-500">
            Not available to you: {access.all_sections.filter(a => !access.sections.some(s => s.key === a.key)).map(a => a.label).join(', ')} — each needs its own seat.
          </p>
        )}
      </fieldset>
      {farms.length > 1 && (
        <fieldset>
          <legend className="mb-1 text-sm font-medium text-neutral-700">Farms <span className="font-normal text-neutral-500">(none ticked = all)</span></legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {farms.map(f => (
              <label key={f.id} className="inline-flex min-w-0 items-center gap-2 text-sm">
                <input type="checkbox" checked={scope.farmIds.includes(f.id)} onChange={() => onChange({ ...scope, farmIds: toggle(scope.farmIds, f.id) })} />
                <span className="break-words">{f.name}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-1" checked={scope.pseudonymise} onChange={e => onChange({ ...scope, pseudonymise: e.target.checked })} />
        <span>
          <span className="font-medium">Hide people’s names</span>
          <span className="block text-xs text-neutral-500">{pseudonymiseHint}</span>
        </span>
      </label>
    </div>
  );
}

const PSEUDO_HINT_PACK = 'Shows “Person 1”, “Person 2” and masks credential numbers — for a packhouse or customer. A GlobalG.A.P. or Freshcare audit needs names: the auditor must match who applied each product to their certificate.';

// ---------------------------------------------------------------------------

function PackTab({ access, farms, tz }: { access: ComplianceAccess; farms: { id: number; name: string }[]; tz: string }) {
  const [scope, setScope] = useState<Scope>({
    start: access.default_period.start, end: access.default_period.end,
    sections: access.sections.map(s => s.key), farmIds: [], pseudonymise: false,
  });
  const [standard, setStandard] = useState<string>('');
  const [title, setTitle] = useState('');
  const [preview, setPreview] = useState<{ report: ComplianceReport; checks: AutoChecks } | null>(null);
  const [packs, setPacks] = useState<CompliancePack[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadPacks = useCallback(() => { complianceService.listPacks().then(setPacks).catch(() => setPacks([])); }, []);
  useEffect(loadPacks, [loadPacks]);

  const valid = scope.sections.length > 0 && scope.start && scope.end && scope.start <= scope.end;

  const run = async (what: 'preview' | 'pack') => {
    setBusy(what);
    setError(null);
    try {
      if (what === 'preview') {
        setPreview(await complianceService.getReport({ ...scope }));
      } else {
        const std = access.standards.find(s => s.key === standard);
        await complianceService.createPack({
          title: title.trim() || `${std ? std.name : 'Compliance pack'} ${scope.start} to ${scope.end}`,
          standard_key: standard || null, period_start: scope.start, period_end: scope.end,
          farm_ids: scope.farmIds.length ? scope.farmIds : null, sections: scope.sections, pseudonymise: scope.pseudonymise,
        });
        loadPacks();
      }
    } catch (e) {
      setError(errorMessage(e, 'That did not work'));
    } finally {
      setBusy(null);
    }
  };

  const download = async (p: CompliancePack, kind: 'pdf' | 'csv') => {
    setBusy(`${kind}${p.id}`);
    try {
      if (kind === 'pdf') await complianceService.downloadPackPdf(p); else await complianceService.downloadPackCsv(p);
    } catch (e) {
      setError(errorMessage(e, 'Download failed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <p className="text-sm text-neutral-600">
          Everything an auditor usually asks for from your spray, training, safety and harvest records, for one period. Preview it, then generate a pack: the pack is frozen, so the copy you hand over can always be reproduced exactly.
        </p>
        <ScopeFields access={access} farms={farms} scope={scope} onChange={setScope} pseudonymiseHint={PSEUDO_HINT_PACK} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Standard (optional)" hint="Includes your self-assessment answers in the pack.">
            <select className={inputClass} value={standard} onChange={e => setStandard(e.target.value)}>
              <option value="">None</option>
              {access.standards.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Pack title (optional)"><input className={inputClass} value={title} maxLength={200} onChange={e => setTitle(e.target.value)} placeholder="e.g. Freshcare recertification 2026" /></Field>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => run('preview')} disabled={!valid || !!busy}>{busy === 'preview' ? 'Gathering…' : 'Preview'}</Button>
          <Button onClick={() => run('pack')} disabled={!valid || !!busy}><FileCheck2 className="mr-1 h-4 w-4" />{busy === 'pack' ? 'Generating…' : 'Generate pack'}</Button>
        </div>
        {error && <p className="text-sm text-red-700">{error}</p>}
      </section>

      {preview && (
        <section className="rounded-xl border border-neutral-200 p-3 sm:p-4">
          <h2 className="mb-2 text-base font-semibold text-neutral-900">Preview</h2>
          <ComplianceReportView report={preview.report} checks={preview.checks} />
        </section>
      )}

      <section>
        <h2 className="mb-2 text-base font-semibold text-neutral-900">Packs</h2>
        {packs.length === 0 ? <p className="text-sm text-neutral-500">No packs generated yet.</p> : (
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {packs.map(p => (
              <li key={p.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="break-words text-sm font-medium text-neutral-900">{p.title}</p>
                  <p className="text-xs text-neutral-500">
                    {formatLocalDateInTz(p.period_start, tz)} – {formatLocalDateInTz(p.period_end, tz)} · generated {formatDateTimeInTz(p.generated_at, tz)}{p.generated_by ? ` by ${p.generated_by}` : ''}
                    {p.pseudonymised ? ' · names hidden' : ''}
                  </p>
                  <p className="truncate font-mono text-[11px] text-neutral-400" title={p.content_sha256}>SHA-256 {p.content_sha256}</p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => download(p, 'pdf')} disabled={!!busy}><Download className="mr-1 h-4 w-4" />PDF</Button>
                  {p.sections.includes('spray') && (
                    <Button size="sm" variant="outline" onClick={() => download(p, 'csv')} disabled={!!busy}><FileSpreadsheet className="mr-1 h-4 w-4" />Spray CSV</Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function AuditorsTab({ access, farms, tz }: { access: ComplianceAccess; farms: { id: number; name: string }[]; tz: string }) {
  const today = getTodayInTz(tz);
  const [list, setList] = useState<AuditEngagement[]>([]);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<Scope>({
    start: access.default_period.start, end: access.default_period.end,
    sections: access.sections.map(s => s.key), farmIds: [], pseudonymise: false,
  });
  const [form, setForm] = useState({ title: '', standard_key: '', auditor_name: '', auditor_email: '', auditor_company: '', until: addDays(today, 30), notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ link: string; email_sent: boolean; name: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => { complianceService.listEngagements().then(setList).catch(() => setList([])); }, []);
  useEffect(load, [load]);

  const maxUntil = addDays(today, access.max_access_days);
  const valid = form.title.trim() && form.auditor_name.trim() && /\S+@\S+\.\S+/.test(form.auditor_email)
    && scope.sections.length && scope.start <= scope.end && form.until > today && form.until <= maxUntil;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const out = await complianceService.createEngagement({
        title: form.title.trim(), standard_key: form.standard_key || null, auditor_name: form.auditor_name.trim(),
        auditor_email: form.auditor_email.trim(), auditor_company: form.auditor_company.trim() || null,
        period_start: scope.start, period_end: scope.end, farm_ids: scope.farmIds.length ? scope.farmIds : null,
        sections: scope.sections, pseudonymised: scope.pseudonymise,
        access_until: dateTimeLocalToISO(`${form.until}T23:59`, tz), notes: form.notes.trim() || null,
      });
      setCreated({ link: out.link, email_sent: out.email_sent, name: form.auditor_name.trim() });
      setOpen(false);
      load();
    } catch (e) {
      setError(errorMessage(e, 'Could not give access'));
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); } catch { setCopied(false); }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2 text-sm text-neutral-600">
        <p>
          Give an auditor read-only access to exactly the records they need, for a set time. They do not get an account:
          they open a link and sign in with a code sent to the address you give. You can see every page they open, they can
          record findings for you to answer, and you can end access at any time.
        </p>
      </div>

      {created && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          <p className="font-medium">Access given to {created.name}.</p>
          <p>{created.email_sent ? 'The link has been emailed to them.' : 'The email could not be sent — pass the link on yourself.'} It only works together with a code sent to their address.</p>
          <div className="mt-2 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
            <code className="min-w-0 break-all rounded bg-white px-2 py-1 text-xs">{created.link}</code>
            <Button size="sm" variant="outline" onClick={() => copy(created.link)}><Copy className="mr-1 h-4 w-4" />{copied ? 'Copied' : 'Copy'}</Button>
          </div>
        </div>
      )}

      {!open ? (
        <Button onClick={() => { setOpen(true); setCreated(null); }}><KeyRound className="mr-1 h-4 w-4" />Give an auditor access</Button>
      ) : (
        <section className="space-y-3 rounded-xl border border-neutral-200 p-3 sm:p-4">
          <h2 className="text-base font-semibold text-neutral-900">New auditor access</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Audit"><input className={inputClass} value={form.title} maxLength={200} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Freshcare recertification 2026" /></Field>
            <Field label="Standard">
              <select className={inputClass} value={form.standard_key} onChange={e => setForm({ ...form, standard_key: e.target.value })}>
                <option value="">None</option>
                {access.standards.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Auditor’s name"><input className={inputClass} value={form.auditor_name} maxLength={160} onChange={e => setForm({ ...form, auditor_name: e.target.value })} /></Field>
            <Field label="Auditor’s email" hint="The sign-in code goes only to this address."><input type="email" className={inputClass} value={form.auditor_email} maxLength={255} onChange={e => setForm({ ...form, auditor_email: e.target.value })} /></Field>
            <Field label="Certification body (optional)"><input className={inputClass} value={form.auditor_company} maxLength={160} onChange={e => setForm({ ...form, auditor_company: e.target.value })} /></Field>
            <Field label="Access until" hint={`At most ${access.max_access_days} days.`}><input type="date" className={inputClass} value={form.until} min={addDays(today, 1)} max={maxUntil} onChange={e => setForm({ ...form, until: e.target.value })} /></Field>
          </div>
          <ScopeFields access={access} farms={farms} scope={scope} onChange={setScope}
            pseudonymiseHint="Only for a reviewer who is not certifying you. A certification auditor needs names to check each operator’s certificate." />
          <Field label="Note (optional, for your records)"><textarea className={inputClass} rows={2} value={form.notes} maxLength={2000} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
          <p className="text-xs text-neutral-500">
            The auditor sees the names, roles and credentials of the people in these records, not their contact details, pay or health information. Your organisation is responsible for telling staff that their records are shared with auditors.
          </p>
          {error && <p className="text-sm text-red-700">{error}</p>}
          <div className="flex flex-wrap gap-2">
            <Button onClick={submit} disabled={!valid || saving}>{saving ? 'Giving access…' : 'Give access and email the link'}</Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-base font-semibold text-neutral-900">Auditor access</h2>
        {list.length === 0 ? <p className="text-sm text-neutral-500">No auditor has been given access.</p> : (
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {list.map(e => (
              <li key={e.id}>
                <Link to={`/compliance/audits/${e.id}`} className="flex flex-col gap-1 p-3 hover:bg-neutral-50 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-medium text-neutral-900">{e.title}</p>
                    <p className="break-words text-xs text-neutral-500">
                      {e.auditor_name}{e.auditor_company ? `, ${e.auditor_company}` : ''} · until {formatDateTimeInTz(e.access_until, tz)}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs text-neutral-600">
                    <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_STYLE[e.status]}`}>{e.status}</span>
                    <span>{e.views ?? 0} views</span>
                    <span>{e.open_findings ?? 0} open findings</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ChecklistTab({ access, tz }: { access: ComplianceAccess; tz: string }) {
  const [standard, setStandard] = useState(access.standards[0]?.key ?? '');
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  const load = useCallback(async () => {
    if (!standard) return;
    setLoading(true);
    setError(null);
    try { setChecklist(await complianceService.getChecklist(standard)); }
    catch (e) { setError(errorMessage(e, 'Could not load the checklist')); }
    finally { setLoading(false); }
  }, [standard]);
  useEffect(() => { load(); }, [load]);

  const save = async (key: string, data: Parameters<typeof complianceService.saveCheckpoint>[2]) => {
    await complianceService.saveCheckpoint(standard, key, data);
    await load();
  };

  const record = async () => {
    setRecording(true);
    try { await complianceService.recordSelfAssessment(standard); await load(); }
    catch (e) { setError(errorMessage(e, 'Could not record it')); }
    finally { setRecording(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <Field label="Standard">
          <select className={inputClass} value={standard} onChange={e => setStandard(e.target.value)}>
            {access.standards.map(s => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </Field>
        <Button variant="outline" onClick={record} disabled={recording || !checklist}>
          <ClipboardCheck className="mr-1 h-4 w-4" />{recording ? 'Recording…' : 'Record this self-assessment'}
        </Button>
      </div>
      {checklist?.period && (
        <p className="text-xs text-neutral-500">
          Checked against records from {formatLocalDateInTz(checklist.period.start, tz)} to {formatLocalDateInTz(checklist.period.end, tz)}.
          {checklist.self_assessments[0] && ` Last recorded ${formatDateTimeInTz(checklist.self_assessments[0].completed_at, tz)}${checklist.self_assessments[0].completed_by ? ` by ${checklist.self_assessments[0].completed_by}` : ''}.`}
        </p>
      )}
      {error && <p className="text-sm text-red-700">{error}</p>}
      {loading && !checklist ? <div className="h-40 animate-pulse rounded-lg bg-neutral-100" /> : checklist && <ChecklistView checklist={checklist} onSave={save} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

const TABS: { key: TabKey; label: string; icon: ReactNode }[] = [
  { key: 'pack', label: 'Season pack', icon: <FileCheck2 className="h-4 w-4" /> },
  { key: 'auditors', label: 'Auditor access', icon: <UserCheck className="h-4 w-4" /> },
  { key: 'checklist', label: 'Self-assessment', icon: <ClipboardCheck className="h-4 w-4" /> },
];

export default function Compliance() {
  const tz = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') as TabKey | null;
  const tab: TabKey = TABS.some(t => t.key === tabParam) ? (tabParam as TabKey) : 'pack';

  const spray = useModuleLicense('spray', 'admin');
  const hr = useModuleLicense('timesheet', 'hr');
  const jobs = useModuleLicense('farm_jobs', ['admin', 'supervisor']);
  const licenceLoading = spray.loading || hr.loading || jobs.loading;
  const anySeat = spray.hasAccess || hr.hasAccess || jobs.hasAccess;

  const [access, setAccess] = useState<ComplianceAccess | null>(null);
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (licenceLoading || !anySeat) return;
    complianceService.getAccess().then(setAccess).catch(e => setError(errorMessage(e, 'Could not load compliance')));
    orchardService.getAllFarms()
      .then(r => setFarms(r.success ? r.data.farms.map(f => ({ id: f.id, name: f.name })) : []))
      .catch(() => setFarms([]));
  }, [licenceLoading, anySeat]);

  const body = useMemo(() => {
    if (!access) return null;
    if (tab === 'pack') return <PackTab access={access} farms={farms} tz={tz} />;
    if (tab === 'auditors') return <AuditorsTab access={access} farms={farms} tz={tz} />;
    return <ChecklistTab access={access} tz={tz} />;
  }, [access, farms, tab, tz]);

  if (licenceLoading) return <div className="mx-auto max-w-5xl px-4 py-6"><div className="h-40 animate-pulse rounded-xl bg-neutral-100" /></div>;
  if (!anySeat) return <ModuleAccessDenied moduleName="Compliance (needs a Spray admin, Timesheet HR or Farm Jobs admin/supervisor seat)" />;

  return (
    <>
      <PageHeader
        title="Compliance & audit"
        subtitle="Season packs, auditor access and self-assessment"
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
      />
      <div className="mx-auto max-w-5xl px-4 py-6">
        <Tabs tabs={TABS} active={tab} onChange={k => setParams(k === 'pack' ? {} : { tab: k }, { replace: true })} className="mb-4" />
        <div className="rounded-xl border border-neutral-200 bg-white p-4 sm:p-6">
          {error && <p className="text-sm text-red-700">{error}</p>}
          {!access && !error ? <div className="h-40 animate-pulse rounded-lg bg-neutral-100" /> : body}
        </div>
      </div>
    </>
  );
}
